<template>
    <vue-select
        ref="select"
        v-bind="$attrs"
        v-on="selectListeners"
        :options="options"
        :filterable="false"
        :create-option="createOption"
    >
        <template v-if="!$scopedSlots.hasOwnProperty('no-options')" #no-options="{ search, searching }">
            <template v-if="searching">
                Hittade inga resultat på <em>{{ search }}</em>.
            </template>
            <em style="opacity: 0.5;" v-else>Sök på produkt</em>
        </template>
        <template v-if="!$scopedSlots.hasOwnProperty('option')" #option="{ ProdID, ProdName, ProdQty, ProdVariant, isNonStockItem, isPhasedOut, isOutOfStock, Stock_future }">
            <div v-if="isTaggable">{{ ProdID }}</div>
            <div v-else-if="showStatus" class="d-flex w-100">
                <div class="mr-auto">#{{ ProdID }} - {{ ProdName }} {{ (ProdQty || ProdVariant ? ' / ' + ProdQty + ProdVariant : '') }}</div>
                <div v-if="isPhasedOut" class="badge badge-danger ml-1">HAR UTGÅTT</div>
                <div v-else-if="isNonStockItem" class="badge badge-warning ml-1">Beställningsvara</div>
                <div v-else-if="isOutOfStock" class="badge badge-danger ml-1">TILLF. SLUT</div>
                <div class="badge ml-1" :class="{ 'badge-success': Stock_future > 0, 'badge-danger': Stock_future <= 0 }">DISPONIBLET {{ Stock_future }}</div>
            </div>
            <div v-else>#{{ ProdID }} - {{ ProdName }} {{ (ProdQty || ProdVariant ? ' / ' + ProdQty + ProdVariant : '') }}</div>
        </template>

        <!-- pass through scoped slots -->
        <template v-for="(_, scopedSlotName) in $scopedSlots" v-slot:[scopedSlotName]="slotData">
            <slot :name="scopedSlotName" v-bind="slotData" />
        </template>

        <!-- pass through normal slots -->
        <template v-for="(_, slotName) in $slots" v-slot:[slotName]>
            <slot :name="slotName" />
        </template>
    </vue-select>
</template>
<script>
import VueSelect from '@components/Wrappers/VueSelect';
import ProductRepository from '@services/repositories/Product/ProductRepository';
import debounce from 'lodash/debounce';

export default {
    name: 'ProductFilter',
    components: {
        VueSelect,
    },
    props: {
        showStatus: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            options: [],
            fetchOptionsDebounced: debounce((search) => {
                this.fetchOptions(search);
            }, 200),
        };
    },
    created() {
        this.fetchOptions(this.$attrs.value);
    },
    beforeUnmount() {
        this.fetchOptionsDebounced.cancel();
    },
    computed: {
        isTaggable() {
            return this.$attrs.taggable !== undefined && this.$attrs.taggable !== false;
        },
        selectListeners() {
            return {
                ...this.$listeners,
                input: this.handleInput,
                search: this.handleSearch,
            };
        },
    },
    methods: {
        createOption(option) {
            const sku = String(option).trim();
            return {
                label: sku,
                ProdID: sku,
            };
        },
        splitSkuList(value) {
            return String(value).split(/[,\s]+/)
                .map((sku) => sku.trim())
                .filter((sku) => sku !== '');
        },
        normalizeTaggableValue(value) {
            if (!Array.isArray(value)) {
                return value;
            }

            return value.reduce((skus, item) => {
                const sku = item && typeof item === 'object' ? item.ProdID : item;

                if (sku !== null && sku !== undefined) {
                    skus.push(...this.splitSkuList(sku));
                }

                return skus;
            }, []);
        },
        handleInput(value) {
            this.$emit('input', this.isTaggable ? this.normalizeTaggableValue(value) : value);
        },
        clearSearch() {
            this.$nextTick(() => {
                const select = this.$refs.select;
                const vueSelect = select && select.$children
                    ? select.$children.find((child) => child.search !== undefined)
                    : null;

                if (vueSelect) {
                    vueSelect.search = '';
                }
            });
        },
        handleSearch(search) {
            if (this.isTaggable && (typeof search === 'string' || search instanceof String)) {
                const selected = this.splitSkuList(search);

                if (selected.length > 1) {
                    const currentValue = Array.isArray(this.$attrs.value) ? this.$attrs.value : [];
                    this.handleInput([...currentValue, ...selected]);
                    this.clearSearch();
                    return;
                }
            }

            this.fetchOptionsDebounced(search);
        },
        async fetchOptions(search = '') {
            const client = new ProductRepository();
            client.where('search', search).setPerPage(8).run().then((res) => {
                this.options = res.data.map((item) => ({
                    label: this.isTaggable ? String(item.ProdID) : '#' + item.ProdID + ' - ' + item.ProdName + (item.ProdQty || item.ProdVariant ? ' / ' + item.ProdQty + item.ProdVariant : ''),
                    ...item,
                }));
            });
        },
    },
};
</script>
